/**
 * VoidInvoiceDialog — Founder-only confirm with mandatory reason.
 *
 * `ConfirmDialog` from `@flint/ui` doesn't accept children, so we
 * compose `DialogRoot` ourselves to embed the reason input. Same
 * danger styling + idempotency contract as ConfirmDialog otherwise.
 *
 * Used by both the list (kebab → Void) and the detail page action
 * row. The reason is required (min 3 chars matches the wire schema).
 */
import { useEffect, useState } from "react";
import { TriangleAlert } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from "@flint/ui";

export interface VoidInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display label (`INV/2026-27/0002` or `Draft`). Used in the body copy. */
  docNumber: string | null;
  pending?: boolean;
  errorMessage?: string;
  /** Fires only when reason passes the min-length client guard. */
  onConfirm: (reason: string) => void;
}

const MIN_REASON_LEN = 3;

export function VoidInvoiceDialog({
  open,
  onOpenChange,
  docNumber,
  pending,
  errorMessage,
  onConfirm,
}: VoidInvoiceDialogProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset on close — next open starts blank.
  useEffect(() => {
    if (!open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LEN;
  const showInline = touched && tooShort;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (tooShort) return;
    onConfirm(trimmed);
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-grid place-items-center h-9 w-9 rounded-md bg-rose-50 text-rose-700 shrink-0"
              >
                <TriangleAlert size={16} />
              </span>
              <div className="flex-1">
                <DialogTitle>Void this invoice?</DialogTitle>
                <DialogDescription>
                  Voiding {docNumber ?? "this invoice"} keeps it in history with a void status. The
                  number is never reused. Tell us why so the audit log captures the reason.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-1.5 mt-4">
            <Label htmlFor="void-reason-input">Reason</Label>
            <Input
              id="void-reason-input"
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setTouched(true);
              }}
              maxLength={500}
              placeholder="e.g. Customer cancelled before dispatch."
              aria-invalid={showInline}
              aria-describedby={showInline ? "void-reason-error" : undefined}
            />
            {showInline ? (
              <p id="void-reason-error" className="text-sm text-bad">
                Reason must be at least {MIN_REASON_LEN} characters.
              </p>
            ) : (
              <p className="text-sm text-muted">Lands in the audit log alongside the void event.</p>
            )}
            {errorMessage ? <p className="text-sm text-bad mt-1">{errorMessage}</p> : null}
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" intent="danger" disabled={pending || tooShort}>
              {pending ? "Voiding…" : "Void invoice"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
